import { makeStyles } from '@material-ui/core/styles';
import React, { ReactElement, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@payactive/app-common';
import { Form, Formik, FormikHelpers } from 'formik';
import { Grid, Paper } from '@material-ui/core';
import DocumentSettings from './DocumentSettings';
import DunningSettings from './DunningSettings';
import { createSchemas, initialValues } from './settingsSchema';
import { AlertContext } from '../../../../components/contexts/WithAlertContext';
import CompanyAccountService from '../../../../services/CompanyAccountService';
import { CompanySettings } from '../../../../types/Company/CompanySettings';
import { Payments } from '../../../../types/Payment/Payment';
import PaymentSettingsService from '../../../../services/PaymentSettingsService';
import PaymentMethodSettings from './PaymentMethodSettings';
import PaymentSettings from './PaymentSettings';
import PaymentMethod = Payments.PaymentMethod;

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '48.5%',
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  saveButton: {
    display: 'flex',
    color: 'green[500]',
    position: 'relative',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  paperLayout: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  paperDesign: {
    padding: theme.spacing(2),
  },
}));

export default function Settings({
  accountSettings,
  dunningService,
}: {
  accountSettings: CompanySettings.Account;
  dunningService: CompanySettings.Dunning;
}): ReactElement {
  const alerting = useContext(AlertContext);

  const { t } = useTranslation('company');
  const { t: tValidation } = useTranslation('validations');
  const classes = useStyles();
  const schema = createSchemas(tValidation, t);

  const _handleSubmit = (
    values: CompanySettings.Account & CompanySettings.Dunning & { paymentMethods: PaymentMethod[] },
    { setSubmitting, resetForm }: FormikHelpers<CompanySettings.Account & CompanySettings.Dunning & { paymentMethods: PaymentMethod[] }>,
  ) => {
    const accountSettingsValues: CompanySettings.Account = {
      bccEmailAddressList: values.bccEmailAddressList,
      bccSelection: values.bccSelection,
      paymentReminderDays: values.paymentReminderDays,
      failedPaymentFee: values.failedPaymentFee,
      paymentReminderEnabled: values.paymentReminderEnabled,
    };
    const dunningSettingsValues: CompanySettings.Dunning = {
      dunningEnabled: values.dunningEnabled,
      daysDunning: values.daysDunning,
      daysReminder: values.daysReminder,
      dunningFee: values.dunningFee,
      maxDunnings: values.maxDunnings,
    };

    const updateCompanyAccountSettingsPromise = CompanyAccountService.updateCompanyAccountSettings(accountSettingsValues);
    const updateDunningSettingsPromise = CompanyAccountService.updateDunningSettings(dunningSettingsValues);

    const updatePaymentMethodsPromise = PaymentSettingsService.getPaymentSettings().then(async (paymentSettings) => {
      let deletedPaymentMethods = paymentSettings.enabledPaymentMethods.filter(
        (paymentMethod) => values.paymentMethods.indexOf(paymentMethod) === -1,
      );
      for (const paymentMethod of deletedPaymentMethods) {
        await PaymentSettingsService.disablePaymentMethod(paymentMethod);
      }

      let newPaymentMethods = values.paymentMethods.filter((paymentMethod) => paymentSettings.enabledPaymentMethods.indexOf(paymentMethod) === -1);
      for (const paymentMethod of newPaymentMethods) {
        await PaymentSettingsService.enablePaymentMethod(paymentMethod);
      }

      return true;
    });

    Promise.all([updateCompanyAccountSettingsPromise, updateDunningSettingsPromise, updatePaymentMethodsPromise])
      .then(([updateCompanyAccountSettingsResponse, updateDunningSettingsResponse, updatePaymentMethodsResponse]) => {
        if (updateCompanyAccountSettingsResponse && updateDunningSettingsResponse && updatePaymentMethodsResponse) {
          setSubmitting(false);
          alerting.sendAlert('success', t('settings updated'));
        } else {
          alerting.sendAlert('error', t('settings update error'));
          window.location.reload();
        }
        resetForm({ values: values });
      })
      .catch(() => {
        alerting.sendAlert('error', t('settings update error'));
        window.location.reload();
      });
  };

  return (
    <>
      <Formik initialValues={initialValues(accountSettings, dunningService)} onSubmit={_handleSubmit} validationSchema={schema}>
        {({ isSubmitting, dirty }) => (
          <>
            <Form autoComplete="on">
              <Grid container spacing={2} alignItems={'stretch'} alignContent={'stretch'}>
                <Grid item xs={6}>
                  <Paper className={classes.paperDesign}>
                    <PaymentSettings />
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper className={classes.paperDesign}>
                    <PaymentMethodSettings />
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper className={classes.paperDesign}>
                    <DunningSettings />
                  </Paper>
                </Grid>

                <Grid item xs={6}>
                  <Paper className={classes.paperDesign}>
                    <DocumentSettings />
                  </Paper>
                </Grid>
              </Grid>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flexStart',
                  padding: '1% 0'
                }}
              >
                <Button type="submit" loading={isSubmitting} disabled={!dirty || isSubmitting} name="submit" label={t('global:save')} />
              </div>
            </Form>
          </>
        )}
      </Formik>
    </>
  );
}
